<template>
  <div class="scan-wrap">
    <i class="fa fa-barcode scan-icon"></i>
    <input
      :id="inputId"
      ref="scanField"
      type="text"
      class="form-control scan-field"
      v-model="barcodeText"
      :placeholder="placeholder"
      autocomplete="off"
      :disabled="disabled || isLoading"
      @keydown.enter.prevent="onScan"
    />
    <span v-if="isLoading" class="spinner-border spinner-border-sm scan-spinner"></span>
  </div>
</template>

<script setup>
import { inject, nextTick, onMounted, ref } from 'vue'
import { useRoute } from 'vue-router'
import handleDropdowns from '@/services/modules/dropdowns'

const props = defineProps({
  apiService: { type: Function, default: null },
  inputId: { type: String, default: 'barcode-scan' },
  placeholder: { type: String, default: 'Scan or type barcode / SKU, press Enter' },
  additionalQuery: { type: Object, default: () => ({ ignore_stock: 1 }) },
  autofocus: { type: Boolean, default: true },
  disabled: { type: Boolean, default: false },
  notFoundMessage: { type: String, default: 'No product found with barcode / SKU' }
})

const emit = defineEmits(['scanned'])

const $route = useRoute()
const showError = inject('showError')
const { fetchProductDropdownList } = handleDropdowns()

const scanField = ref(null)
const barcodeText = ref('')
const isLoading = ref(false)

const focus = () => {
  nextTick(() => {
    if (! scanField.value) return
    scanField.value.focus()
  })
}

const searchProducts = (query) => {
  if (props.apiService) return props.apiService(query)
  return fetchProductDropdownList(query)
}

const getScanQuery = (code) => {
  const queryObject = {
    ...props.additionalQuery,
    company_id: $route.params.companyId,
    barcode_sku: code
  }
  return '?' + new URLSearchParams(queryObject).toString()
}

const onScan = async () => {
  const code = (barcodeText.value || '').trim()
  if (! code) return

  isLoading.value = true
  try {
    const res = await searchProducts(getScanQuery(code))
    if (! res.status || ! res.data || ! res.data.length) {
      showError(`${props.notFoundMessage}: ${code}`)
      return
    }
    emit('scanned', res.data[0])
  } catch (err) {
    showError('Failed to fetch product by barcode / SKU')
  } finally {
    isLoading.value = false
    barcodeText.value = ''
    focus()
  }
}

onMounted(() => {
  if (! props.autofocus) return
  focus()
})

defineExpose({
  focus
})
</script>

<style scoped>
.scan-wrap {
  position: relative;
}
.scan-icon {
  position: absolute;
  left: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #b58a1d;
  pointer-events: none;
}
.scan-spinner {
  position: absolute;
  right: 12px;
  top: 50%;
  margin-top: -0.5rem;
  color: #b58a1d;
}
.scan-field {
  background: #fffbe6;
  border-color: #f2d675;
  font-weight: 600;
  padding-left: 34px;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.scan-field:focus {
  background: #fffbe6;
  border-color: #d4a72c;
  box-shadow: 0 0 0 3px rgba(212, 167, 44, 0.15);
}
</style>
